import React, { useEffect, useState } from 'react';
import { AiMode } from '../../../api/cafAutomation';
import { Icon } from '../Icon';
import { KebabMenu, MenuItem } from '../KebabMenu';
import { AiSwitchCard, SwitchConfirm } from './AiSwitchCard';
import { OLD_BACKEND, PAGE_SENTENCE } from './automationCopy';
import { SettingsModal, useDaysSetting } from './SettingsModal';
import { SwitchHistoryDrawer } from './SwitchHistoryDrawer';
import { TechDrawer } from './TechDrawer';
import { useAutomationOverview } from './useAutomationOverview';
import { PauseAllConfirm, TeachTarget } from './WorkflowConfirms';
import { WorkflowsCard } from './WorkflowsCard';
import { WorkflowWindow } from './WorkflowWindow';
import { WorkingCard } from './WorkingCard';
import { AttentionCard } from './AttentionCard';
import { AutomationRunsDrawer } from './AutomationRunsDrawer';

const CURRENT_SCOPE = 'caf-workflows-1';

type Dialog =
  | { kind: 'switch'; to: AiMode }
  | { kind: 'history' }
  | { kind: 'settings' }
  | { kind: 'tech' }
  | { kind: 'pauseAll'; pause: boolean }
  | { kind: 'runs'; attention: boolean }
  | null;

interface Props {
  onBack: () => void;
  initialWorkflowId: string | null;
  onOpenTeach: (target: TeachTarget | null) => void;
  onSwitchChanged: () => void;
}

export const AutomationPage: React.FC<Props> = ({ onBack, initialWorkflowId, onOpenTeach, onSwitchChanged }) => {
  const [days, setDays] = useDaysSetting();
  const overview = useAutomationOverview(days, CURRENT_SCOPE);
  const data = overview.status === 'ready' &&
    (overview.data?.evidence?.scope === CURRENT_SCOPE || !overview.data?.evidence)
    ? overview.data : null;
  const [dialog, setDialog] = useState<Dialog>(null);
  const [windowFor, setWindowFor] = useState<{ id: string | null } | null>(initialWorkflowId ? { id: initialWorkflowId } : null);
  const [listKey, setListKey] = useState(0);
  useEffect(() => {
    setWindowFor(initialWorkflowId ? { id: initialWorkflowId } : null);
  }, [initialWorkflowId]);
  const close = () => setDialog(null);
  const refresh = () => { overview.reload(); setListKey((key) => key + 1); };
  const showWorkflows = () => {
    const element = document.getElementById('cur-workflows');
    if (element && typeof element.scrollIntoView === 'function') element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const menu: MenuItem[] = data ? [
    { key: 'settings', label: 'Settings', icon: 'wrench', onSelect: () => setDialog({ kind: 'settings' }) },
    { key: 'history', label: 'History of the AI switch', icon: 'clock', onSelect: () => setDialog({ kind: 'history' }) },
    data.workflows_paused
      ? { key: 'resume', label: 'Resume workflows…', icon: 'play', separatorBefore: true, onSelect: () => setDialog({ kind: 'pauseAll', pause: false }) }
      : { key: 'pause', label: 'Pause all workflows…', icon: 'pause', danger: true, separatorBefore: true, onSelect: () => setDialog({ kind: 'pauseAll', pause: true }) },
  ] : [];

  return (
    <div>
      <div className="cur-auto-head">
        <button type="button" className="cur-back" onClick={onBack}><Icon name="chev-r" size="sm" />Back to Teach the AI</button>
        <span className="cur-grow">{PAGE_SENTENCE}</span>
        <span className="cur-pill cur-pill--grey">Admins only</span>
        {data && <KebabMenu label="Automation menu" items={menu} />}
      </div>
      {overview.status === 'missing' && <div className="cur-alert cur-alert--info" role="status">{OLD_BACKEND}</div>}
      {overview.status === 'loading' && <div className="cur-empty" role="status">Loading…</div>}
      {overview.status === 'ready' && !data && <div className="cur-empty" role="status">Loading…</div>}
      {overview.status === 'error' && (
        <div className="cur-alert cur-alert--error" role="alert">
          <span className="cur-grow">Couldn't load Automation.</span>
          <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={overview.reload}>Try again</button>
        </div>
      )}
      {data && (
        <div className="cur-stack">
          {overview.failed && <p className="cur-hint">Couldn&apos;t refresh. Showing the last numbers.</p>}
          <AiSwitchCard overview={data} onRequest={(to) => setDialog({ kind: 'switch', to })} />
          <AttentionCard overview={data} onOpenAttention={() => setDialog({ kind: 'runs', attention: true })}
            onOpenRuns={() => setDialog({ kind: 'runs', attention: false })} />
          <WorkingCard overview={data} days={days} failed={overview.failed} onRetry={overview.reload}
            onRequestWatching={() => setDialog({ kind: 'switch', to: 'watching' })}
            onOpenWorkflow={(id) => setWindowFor({ id })} />
          <div id="cur-workflows">
            <WorkflowsCard days={days} scope={CURRENT_SCOPE} runsAvailable={Boolean(data.capabilities?.runs && data.activation)} reloadKey={listKey} onOpen={(id) => setWindowFor({ id })} onOpenTeach={onOpenTeach}
              onResumeAll={() => setDialog({ kind: 'pauseAll', pause: false })} onChanged={overview.reload} />
          </div>
        </div>
      )}
      {data && dialog?.kind === 'switch' && (
        <SwitchConfirm overview={data} to={dialog.to} onClose={close} onSaved={() => { refresh(); onSwitchChanged(); }}
          onReload={refresh} onSeeWorkflows={() => { close(); showWorkflows(); }} />
      )}
      {dialog?.kind === 'history' && <SwitchHistoryDrawer onClose={close} />}
      {data && dialog?.kind === 'settings' && (
        <SettingsModal sendLimit={data.send_limit} days={days} onDays={setDays} onClose={close} onSaved={overview.reload}
          onTechnical={() => setDialog({ kind: 'tech' })} />
      )}
      {data && dialog?.kind === 'tech' && <TechDrawer overview={data} onClose={close} />}
      {data && dialog?.kind === 'pauseAll' && (
        <PauseAllConfirm pause={dialog.pause} revision={data.control_revision} onClose={close} onDone={refresh} onReload={overview.reload} />
      )}
      {dialog?.kind === 'runs' && <AutomationRunsDrawer attention={dialog.attention} onClose={close} />}
      {data && windowFor && (
        <WorkflowWindow key={windowFor.id ?? 'new'} workflowId={windowFor.id} onClose={() => setWindowFor(null)}
          onSaved={refresh} onOpenTeach={() => onOpenTeach(null)} />
      )}
    </div>
  );
};
